import { useCallback, memo } from "react";
import { Portal, Snackbar, Alert, Button } from "@mui/material";
// types
import type { SyntheticEvent } from "react";
import type { AlertColor, SnackbarCloseReason, ButtonProps } from "@mui/material";

type Props = Readonly<{
	autoHideDuration?: number | null;
	severity: AlertColor;
	message: string;
	actionButtonProps?: Omit<ButtonProps, "variant" | "color" | "size">;
	actionButtonText?: string;
} & (
	{
		open: boolean;
		onClose: (open: false) => void;
	} | {
		open: number | string | undefined;
		onClose: (open: undefined) => void;
	}
)>;

const Toast = ({ autoHideDuration = undefined, severity, message, actionButtonProps, actionButtonText, open, onClose }: Props) => {
	const isBoolean = typeof open === "boolean";
	const returnValue = isBoolean ? false : undefined;

	const handleSnackbarClose = useCallback((_event: SyntheticEvent | Event, reason: SnackbarCloseReason) => {
		if (reason === "clickaway") {
			return;
		}
		onClose(returnValue);
	}, [returnValue, onClose]);

	const handleAlertClose = useCallback(() => (onClose(returnValue)), [returnValue, onClose]);

	return (
		<Portal>
			<Snackbar
				key={isBoolean ? undefined : open}
				autoHideDuration={autoHideDuration}
				open={isBoolean ? open : open !== undefined}
				onClose={handleSnackbarClose}
			>
				<Alert
					severity={severity}
					action={(actionButtonProps && actionButtonText) ? <Button color="inherit" size="small" {...actionButtonProps}>{actionButtonText}</Button> : undefined}
					onClose={handleAlertClose}
					slotProps={{
						message: {
							sx: { wordBreak: "break-word" },
						},
					}}
				>
					{message}
				</Alert>
			</Snackbar>
		</Portal>
	);
};

export default memo(Toast);
